import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';

import type { ICountryFeesSettingsDialogSave } from '@modules/admin-fees/components/VCountryFeesSettingsDialog/types';
import type {
  AdminFeesAasmState,
  IAdminFeesFilterItem,
  IAdminFeesFilterItemErrors,
  IAdminFeesFilterTabs,
  IAdminFeesForm,
  IAdminFeesMerchant,
  ICountriesFeesItem,
  ICountriesFeesItemData,
} from '@modules/admin-fees/types';
import type { ICountry } from '@modules/countries/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES } from '@modules/admin-fees/constants';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { EAasmState } from '@modules/core/enums';
import { useI18n } from '@modules/i18n/composables';
import { isNumber } from '@utils/guards';

export interface IAdminFeesMerchantAndCountryFilters {
  merchantsOptions: ComputedRef<IFormSelectInputOption<number>[]>;
  invalidAdminFeesFilters: ComputedRef<IAdminFeesFilterItem [] | undefined>;
  adminFeesMerchantTabs: ComputedRef<IAdminFeesFilterTabs[]>;
  filteredCountryFees: ComputedRef<ICountriesFeesItem[]>;
  displayFilterErrors: ComputedRef<string[] | undefined>;
  onConfirmRemove(index: number, callback: (index: number) => void): void;
  onMerchantFilterRemove(merchantId: number): void;
  onCountryFilterRemove(index: number): void;
  onAddMerchant(merchant_id: number): void;
  onAddCountryFilter(): void;
  onCountryFeesSave(payload: ICountryFeesSettingsDialogSave): void;
}

export default function useAdminFeesMerchantAndCountryFilters(
  formPayload: Ref<IAdminFeesForm>,
  merchantList: Ref<IAdminFeesMerchant[] | undefined>,
  countriesList: Ref<ICountry[] | undefined>,
  activeFilterMerchantId: Ref<number | undefined>,
): IAdminFeesMerchantAndCountryFilters {
  const maxCountryIndex = ref(0);

  const { getTranslation } = useI18n();
  const { confirmDelete } = useConfirmDialogs();

  const merchantsOptions = computed<IFormSelectInputOption<number>[]>(() => (
    (merchantList.value ?? [])
      .filter(({ id }) => {
        return !formPayload.value?.admin_fees_merchant_filters
          .filter(({ _destroy }) => !_destroy)
          .map(({ merchant_id }) => merchant_id)
          .includes(id);
      })
      .map(({ id, value }) => ({
        value: id,
        label: value,
      }))
  ));

  const adminFeesFiltersWithErrors = computed<IAdminFeesFilterItem[]>(() => (
    formPayload.value?.admin_fees_merchant_filters.map((item) => convertFilterItemToItemWithErrors(item))
  ));

  const activeAdminFeesFilter = computed<IAdminFeesFilterItem | undefined>(() => (
    adminFeesFiltersWithErrors.value.find(({ merchant_id, _destroy }) => merchant_id === activeFilterMerchantId.value && !_destroy)
  ));

  const invalidAdminFeesFilters = computed<IAdminFeesFilterItem[] | undefined>(() => (
    adminFeesFiltersWithErrors.value.filter((item) => !!item.errors)
  ));

  const adminFeesMerchantTabs = computed<IAdminFeesFilterTabs[]>(() => {
    const tabs: IAdminFeesFilterTabs[] = [];

    formPayload.value?.admin_fees_merchant_filters.forEach((item) => {
      if (!item._destroy) {
        tabs.push({
          merchant_id: item.merchant_id,
          label: merchantList.value?.find(({ id }) => id === item.merchant_id)?.value || '',
          isActive: item.merchant_id === activeFilterMerchantId.value,
          isAllMerchants: item.isAllMerchants,
        });
      }
    });

    return tabs;
  });

  const activeCountryFees = computed<ICountriesFeesItemData[]>(() => (
    activeAdminFeesFilter.value?.admin_fees_country_filters ?? []
  ));

  const filteredCountryFees = computed<ICountriesFeesItem[]>(() => {
    const items: ICountriesFeesItem[] = [];

    activeCountryFees.value.forEach((item, index) => {
      const isAllCountries = item.aasm_state === EAasmState.DEFAULT;

      if (!item._destroy) {
        items.push({
          ...item,
          index,
          emptyLabel: isAllCountries
            ? getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'FORM_COUNTRY_FEES_ALL_COUNTRIES').value
            : getCountryNames(item.value),
          name: isAllCountries ? '' : item.name,
          isAllCountries,
          errors: getCountryFilterErrors(item),
        });
      }
    });

    return items;
  });

  const displayFilterErrors = computed<string[] | undefined>(() => {
    const errorNames = adminFeesFiltersWithErrors.value
      .filter((item) => item.errors?.invalid && item.merchant_id !== activeFilterMerchantId.value)
      .map((item) => merchantList.value?.find(({ id }) => id === item.merchant_id)?.value);

    return errorNames.length > 0
      ? [`${getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'FORM_BRANDS_SOME_FILTERS_INVALID').value}: ${errorNames.join(', ')}`]
      : undefined;
  });

  function getCountryNames(countryCodes: number[]): string {
    return countriesList.value?.filter(({ country_code }) => countryCodes.includes(country_code)).map(({ name }) => name).join(', ') || '';
  }

  function getCountryFilterErrors(item: ICountriesFeesItemData): string[] {
    const errors = [];

    if (item.aasm_state === EAasmState.CUSTOM && !item._destroy && item.value.length === 0) {
      errors.push(getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'FORM_COUNTRY_FEES_IS_INVALID').value);
    }

    return errors;
  }

  function isSomeCountryFilterInvalid(list: ICountriesFeesItemData[]): boolean {
    return list
      .filter((item) => item.aasm_state === EAasmState.CUSTOM && !item._destroy)
      .map((item) => item.value.length > 0 && isNumber(item.deposit_percent) && isNumber(item.withdrawals_percent))
      .some((isValid) => !isValid);
  }

  function getFilterItemErrors(item: IAdminFeesFilterItem): IAdminFeesFilterItemErrors {
    const errors: IAdminFeesFilterItemErrors = {};

    if (!item._destroy && isSomeCountryFilterInvalid(item.admin_fees_country_filters)) {
      errors.invalid = true;
    }

    return errors;
  }

  function convertFilterItemToItemWithErrors(item: IAdminFeesFilterItem): IAdminFeesFilterItem {
    const errors: IAdminFeesFilterItemErrors = getFilterItemErrors(item);

    return {
      ...item,
      errors: Object.keys(errors).length > 0 ? errors : undefined,
    };
  }

  function onConfirmRemove(index: number, callback: (index: number) => void): void {
    confirmDelete({
      group: 'confirmDialog',
      header: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'DIALOG_REMOVE_TITLE').value,
      message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'DIALOG_REMOVE_CONTENT').value,
      acceptLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'DIALOG_REMOVE_YES').value,
      rejectLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'DIALOG_REMOVE_NO').value,
      accept: () => {
        callback(index);
      },
    });
  }

  function onMerchantFilterRemove(merchantId: number): void {
    const index = formPayload.value?.admin_fees_merchant_filters.findIndex((item) => item.merchant_id === merchantId && !item._destroy);
    const activeFilter = formPayload.value?.admin_fees_merchant_filters[index];

    if (activeFilter) {
      if (activeFilter.id) {
        formPayload.value?.admin_fees_merchant_filters.splice(index, 1, {
          ...activeFilter,
          _destroy: 1,
        });
      } else {
        formPayload.value?.admin_fees_merchant_filters.splice(index, 1);
      }
    }

    if (activeFilterMerchantId.value === merchantId) {
      activeFilterMerchantId.value = formPayload.value?.admin_fees_merchant_filters?.[0].merchant_id;
    }
  }

  function onCountryFilterRemove(index: number): void {
    const activeFilter = activeAdminFeesFilter.value?.admin_fees_country_filters[index];

    if (activeFilter) {
      if (activeFilter.id) {
        activeAdminFeesFilter.value?.admin_fees_country_filters.splice(index, 1, {
          ...activeFilter,
          _destroy: 1,
        });
      } else {
        activeAdminFeesFilter.value?.admin_fees_country_filters.splice(index, 1);
      }
    }
  }

  function onAddMerchant(merchant_id: number): void {
    formPayload.value?.admin_fees_merchant_filters.push({
      merchant_id,
      isAllMerchants: false,
      admin_fees_country_filters: [createCountryFilter(EAasmState.DEFAULT)],
    });
    activeFilterMerchantId.value = merchant_id;
  }

  function createCountryFilter(aasm_state: AdminFeesAasmState): ICountriesFeesItem {
    maxCountryIndex.value = activeAdminFeesFilter.value?.admin_fees_country_filters?.length || 0;

    return {
      aasm_state,
      value: [],
      deposit_percent: 0,
      withdrawals_percent: 0,
      name: '',
      isAllCountries: false,
      index: maxCountryIndex.value,
    };
  }

  function onAddCountryFilter(): void {
    if (!activeAdminFeesFilter.value)
      return;

    activeAdminFeesFilter.value.admin_fees_country_filters.push(createCountryFilter(EAasmState.CUSTOM));
  }

  function onCountryFeesSave({ index, data }: ICountryFeesSettingsDialogSave): void {
    const activeFilter = activeAdminFeesFilter.value?.admin_fees_country_filters[index];

    if (activeFilter) {
      activeAdminFeesFilter.value?.admin_fees_country_filters.splice(index, 1, {
        ...activeFilter,
        ...data,
      });
    }
  }

  return {
    merchantsOptions,
    invalidAdminFeesFilters,
    adminFeesMerchantTabs,
    filteredCountryFees,
    displayFilterErrors,
    onConfirmRemove,
    onMerchantFilterRemove,
    onCountryFilterRemove,
    onAddMerchant,
    onAddCountryFilter,
    onCountryFeesSave,
  };
}
